import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { withRouter } from 'react-router-dom';
import { get, toNumber } from 'lodash';
import { Button, Confirm, Message } from 'semantic-ui-react';
import { graphql } from 'react-apollo';
import { connect } from 'react-redux';
import client from '../../apollo';

import Form from './GroupException/Form';
import GroupExceptionList from './List';
import customerDealerGroupQuery from '../../graphql/customerDealerGroupQuery';
import { setPage } from '../../data/page/actions';
import withPagination from '../../containers/WithPagination';
import autoCancellationExceptionListQuery from '../../graphql/autoCancellationExceptionListQuery';
import createAutoCancellationExceptionMutation from '../../graphql/createAutoCancellationExceptionMutation';
import updateAutoCancellationExceptionMutation from '../../graphql/updateAutoCancellationExceptionMutation';
import deleteAutoCancellationExceptionMutation from '../../graphql/deleteAutoCancellationExceptionMutation';

const defaultGroupException = {
  id: 0,
  groupException: '',
  gracePeriod: '',
  exceptionEndTime: '',
  createdBy: '',
  createdAt: '',
  lastUpdatedBy: '',
  lastUpdatedAt: '',
};

const fieldsToCompare = ['gracePeriod', 'exceptionEndTime'];
class GroupExceptions extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isModalOpen: false,
      formSubmitting: false,
      selectedGroupException: null,
      isDeleteConfirmation: false,
      flashMessage: null,
      formError: null,
      groups: [],
      isNegativeFlashMessage: false,
    };
  }

  componentDidMount() {
    this.groupNameQuery();
  }

  createNewGroupException = () => {
    this.setState({
      isModalOpen: true,
      selectedGroupException: defaultGroupException,
    });
  };

  onFormCloseHandler = () => {
    this.setState({
      isModalOpen: false,
      selectedGroupException: null,
      formError: null,
      formSubmitting: null,
    });
  };

  static isChange(values, selectedGroupException, fields) {
    return fields.some(
      field => values[field] !== selectedGroupException[field]
    );
  }

  onFormSubmitHandler = async values => {
    const {
      createAutoCancellationExceptionMutation: createMutation,
      updateAutoCancellationExceptionMutation: updateMutation,
    } = this.props;
    const { selectedGroupException } = this.state;
    this.setState({
      formSubmitting: true,
    });
    const { action } = values;
    switch (action) {
      case 'Create':
        await this.onExcuteMutationGroupException(
          createMutation,
          'createAutoCancellationException',
          values,
          action
        );
        break;
      case 'Update':
        if (GroupExceptions.isChange(values, selectedGroupException, fieldsToCompare)) {
          await this.onExcuteMutationGroupException(
            updateMutation,
            'updateAutoCancellationException',
            values,
            action
          );
        } else {
          // unchanged
          this.onFormCloseHandler();
        }
        break;
      default:
        break;
    }
  };

  onFailedSubmitGroupException = (action, errorMessage) => {
    if (action === 'Delete') {
      this.setState({
        isDeleteConfirmation: false,
        flashMessage: `Unsuccessfully Deleted Group Exception with error: ${errorMessage}`,
        isNegativeFlashMessage: true,
      });
    } else {
      this.setState({
        formSubmitting: false,
        formError: errorMessage,
      });
    }
  };

  onExcuteMutationGroupException = async(
    mutation,
    mutationResultName,
    values,
    action
  ) => {
    const { setPage: setPageAction, data } = this.props;
    const input = GroupExceptions.getCreateExpiredVraGracePeriodMutationInput(
      action,
      values
    );
    await mutation({
      variables: { ...input },
    })
      .then(({ data: mutationData }) => {
        const { success, msg } = mutationData[mutationResultName];
        if (success) {
          this.successSubmitGroupException(action);
          this.onFormCloseHandler();
          if (action === 'Delete') {
            this.setState({
              isDeleteConfirmation: false,
            });
          }
          setPageAction({});
          data.refetch();
        } else {
          this.onFailedSubmitGroupException(action, msg);
        }
      })
      .catch(error => {
        this.onFailedSubmitGroupException(
          action,
          Array.isArray(error) ? error : error.message
        );
      });
  };

  static getCreateExpiredVraGracePeriodMutationInput(action, values) {
    return {
      exceptionType: 'GROUP',
      exceptionSourceId: toNumber(values.groupId),
      gracePeriod: toNumber(values.gracePeriod),
      exceptionEndTime: values.exceptionEndTime,
      id: values.id ? toNumber(values.id) : 0,
    };
  }

  successSubmitGroupException = action => {
    this.setState({
      formSubmitting: false,
      formError: null,
      flashMessage: `Successfully ${action}d Group Exception.`,
    });
  };

  onSelectGroupException = (action, groupException) => {
    this.setState({
      selectedGroupException: groupException,
      flashMessage: null,
      isNegativeFlashMessage: false,
    });

    switch (action) {
      case 'Edit':
        this.setState({
          isModalOpen: true,
        });
        break;
      case 'Delete':
        this.setState({
          isDeleteConfirmation: true,
        });
        break;
      default:
        this.setState({
          selectedGroupException: null,
        });
    }
  };

  handleDeleteCancel = () => {
    this.setState({ isDeleteConfirmation: false });
  };

  handleDeleteConfirm = async() => {
    const {
      selectedGroupException: { id },
    } = this.state;
    const { deleteAutoCancellationExceptionMutation: deleteMutation } = this.props;
    const action = 'Delete';
    try {
      await this.onExcuteMutationGroupException(
        deleteMutation,
        'deleteAutoCancellationException',
        { id },
        action
      );
    } catch (error) {
      this.onFailedSubmitGroupException(
        action,
        Array.isArray(error) ? error : error.message
      );
    }
  };

  groupNameQuery = () => {
    client
      .query({
        query: customerDealerGroupQuery,
        variables: {
          page: 1,
          size: 200,
        },
      })
      .then(({ data }) => {
        this.setState({
          groups: get(data, 'customerDealerGroupList'),
        });
      })
      .catch(() => {
        this.setState({ groups: [] });
      });
  };

  handleMessageDismiss = () => {
    this.setState({ flashMessage: null });
  };

  render() {
    const {
      data: { autoCancellationExceptionList },
    } = this.props;
    const {
      isModalOpen,
      formSubmitting,
      selectedGroupException,
      isDeleteConfirmation,
      flashMessage,
      groups,
      formError,
      isNegativeFlashMessage,
    } = this.state;

    return (
      <div>
        {flashMessage && (
          <Message
            onDismiss={this.handleMessageDismiss}
            content={flashMessage}
            positive
            negative={isNegativeFlashMessage}
          />
        )}
        <Button inverted color='green' onClick={this.createNewGroupException}>
          New Group Exception
        </Button>
        <br />
        <br />
        <GroupExceptionList
          onSelectException={this.onSelectGroupException}
          exceptions={autoCancellationExceptionList}
          exceptionType='GROUP'
        />
        {isModalOpen && (
          <Form
            isModalOpen={isModalOpen}
            onCloseHandler={this.onFormCloseHandler}
            onSubmitHandler={this.onFormSubmitHandler}
            submitting={formSubmitting}
            groups={groups}
            groupException={selectedGroupException}
            error={formError}
          />
        )}
        <Confirm
          open={isDeleteConfirmation}
          onCancel={this.handleDeleteCancel}
          onConfirm={this.handleDeleteConfirm}
        />
      </div>
    );
  }
}

GroupExceptions.propTypes = {
  createAutoCancellationExceptionMutation: PropTypes.func.isRequired,
  updateAutoCancellationExceptionMutation: PropTypes.func.isRequired,
  deleteAutoCancellationExceptionMutation: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  data: PropTypes.shape({
    autoCancellationExceptionList: PropTypes.arrayOf(PropTypes.object),
    refetch: PropTypes.func,
    user: PropTypes.object,
  }).isRequired,
};

const mapDispatchToProps = dispatch => ({
  setPage: () => {
    dispatch(setPage());
  },
});

const mapStateToProps = state => {
  const {
    data: { user },
  } = state;
  return {
    user,
  };
};

export default compose(
  withRouter,
  withPagination(
    autoCancellationExceptionListQuery,
    'autoCancellationExceptionList',
    { exceptionTypes: 'GROUP' }
  ),
  graphql(createAutoCancellationExceptionMutation, {
    name: 'createAutoCancellationExceptionMutation',
  }),
  graphql(updateAutoCancellationExceptionMutation, {
    name: 'updateAutoCancellationExceptionMutation',
  }),
  graphql(deleteAutoCancellationExceptionMutation, {
    name: 'deleteAutoCancellationExceptionMutation',
  }),
  connect(mapStateToProps, mapDispatchToProps)
)(GroupExceptions);
